import {
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
} from '@mui/material';
import ActionButton from 'common/components/ActionButton';
import CustomMenuItem from 'common/components/CustomMenuItem';
import CustomSelectValidator from 'common/components/CustomSelectValidators';
import CustomTextValidator from 'common/components/CustomTextValidators';
import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { useFormFields } from 'libs/hooksLib';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { getReminders, getRemindersSavePending } from 'redux/slices/iCalendarV4/reminderSlice';
import { cancelCloseNestedDialogAction, closeNestedDialogAction } from 'redux/slices/nestedDialogSlice';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    eventInputSelector: {
        margin: theme.spacing(0.5),
        backgroundColor: 'none',
        '& > :first-of-type': {
            fontSize: theme.typography.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            width: 'auto',
            borderRadius: '0 !important',
            borderLeft: `1px solid ${theme.palette.secondary.light} !important`,
            '& > fieldset': {
                border: 'none !important',
            },
        },
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
            overflow: 'hidden',
        },
        '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: theme.palette.default.main,
        },
        '& input': {
            position: 'absolute !important',
        },
    },
}));

const TARGET_ACTOR_TYPES = ['all', 'agent', 'guest'];

const ICalendarReminderForm = ({ action, reminder, onSubmit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [openCancelDlg, setOpenCancelDlg] = useState(false);
    const savePending = useSelector(getRemindersSavePending);
    const cancelConfirmTitle = t('user.userInviteForm.cancelConfirm');
    const cancelConfirmMessage = t('user.userInviteForm.cancelMessage');
    const reminders = useSelector(getReminders);

    const initialForm = useMemo(() => {
        return {
            time: reminder.time,
            unit: reminder.unit,
            direction: reminder.direction,
            deliveryMethod: reminder.deliveryMethod,
            enabled: reminder.enabled,
            targetActorType: reminder.targetActorType,
        };
    }, [reminder]);

    const [fields, handleFieldChange, resetForm] = useFormFields(initialForm);

    const userDidChange = useCallback(() => {
        return !isEqual(initialForm, fields);
    }, [initialForm, fields]);

    const restart = useCallback(() => {
        resetForm(initialForm);
    }, [resetForm, initialForm]);

    const finalCancel = useCallback(() => {
        restart();
        dispatch(closeNestedDialogAction());
    }, [dispatch, restart]);

    const manageCancel = useCallback(() => {
        if (!userDidChange()) {
            finalCancel();
        }
        setOpenCancelDlg(true);
    }, [userDidChange, finalCancel]);

    const cancelDlg = () => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseNestedDialogAction());
    };

    const cancel = (event) => {
        event.preventDefault();
        manageCancel();
    };

    const checkExistRule = useMemo(() => {
        return reminders.some(
            (item) =>
                item.id !== reminder.id &&
                item.time === fields.time &&
                item.unit === fields.unit &&
                item.direction === fields.direction &&
                item.deliveryMethod === fields.deliveryMethod,
        );
    }, [reminders, reminder, fields]);

    function handleSubmit(event) {
        event.preventDefault();
        if (checkExistRule) {
            enqueueSnackbar(t('reminder.form.existRule'), {
                variant: 'error',
            });
            return;
        }
        onSubmit({
            reminderDto: {
                ...fields,
            },
            onSuccess: () => {
                enqueueSnackbar(reminder.id ? t('reminder.form.successUpdate') : t('reminder.form.successCreate'), {
                    variant: 'success',
                });
                finalCancel();
            },
            onFailure: () => {
                enqueueSnackbar(t('app.common.error'), {
                    variant: 'error',
                });
            },
        });
    }

    const handleError = () => {
        enqueueSnackbar(t('user.userInviteForm.formError'), {
            variant: 'error',
        });
    };

    return (
        <LayoutForm
            size="small"
            title={action}
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: cancelConfirmTitle,
                messageDlg: cancelConfirmMessage,
            }}
        >
            <ValidatorForm
                onSubmit={handleSubmit}
                onError={handleError}
                className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden)}
            >
                <Stack direction="column" spacing={2} className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone)}>
                    <FormGroup>
                        <FormLabel>{t('game.gameItem.time')}</FormLabel>
                        <CustomTextValidator
                            name="time"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            type={'number'}
                            min={0}
                            useRegExpText={false}
                            value={fields.time}
                            onChange={handleFieldChange}
                            validators={[`minNumber:0`, 'required']}
                            errorMessages={[t('event.eventFormReminders.minValue'), t('app.common.required')]}
                            InputProps={{
                                endAdornment: (
                                    <FormGroup>
                                        <TextField
                                            name="unit"
                                            select
                                            className={classes.eventInputSelector}
                                            value={fields.unit}
                                            onChange={handleFieldChange}
                                            variant="outlined"
                                            size="small"
                                        >
                                            <MenuItem key="hour" value="h">
                                                {t('event.eventFormReminders.hours').toUpperCase()}
                                            </MenuItem>
                                            <MenuItem key="minute" value="m">
                                                {t('event.eventFormReminders.minutes').toUpperCase()}
                                            </MenuItem>
                                            <MenuItem key="second" value="s">
                                                {t('event.eventFormReminders.seconds').toUpperCase()}
                                            </MenuItem>
                                        </TextField>
                                    </FormGroup>
                                ),
                            }}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={fields.enabled}
                                    onChange={handleFieldChange}
                                    name="enabled"
                                    color="primary"
                                />
                            }
                            label={<FormLabel>{t('remainder.form.enabled')}</FormLabel>}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('reminder.targetActorTypes')}</FormLabel>
                        <CustomSelectValidator
                            name="targetActorType"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.targetActorType}
                            select
                            onChange={handleFieldChange}
                            validators={['required']}
                            errorMessages={[t('app.common.required')]}
                        >
                            {TARGET_ACTOR_TYPES.map((type) => (
                                <CustomMenuItem key={type} value={type} noBorder={true}>
                                    {t(`reminder.targetActorTypes.${type}`)}
                                </CustomMenuItem>
                            ))}
                        </CustomSelectValidator>
                    </FormGroup>

                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('iCalendar.reminder.deliveryMethod')}</FormLabel>
                        <RadioGroup name="deliveryMethod" value={fields.deliveryMethod} onChange={handleFieldChange}>
                            <Stack direction="row" spacing={1}>
                                <FormControlLabel
                                    value="async_message"
                                    control={<Radio />}
                                    label={t('iCalendar.reminder.deliveryMethod.asyncMessage')}
                                />
                                <FormControlLabel
                                    value="realtime_socket"
                                    control={<Radio />}
                                    label={t('iCalendar.reminder.deliveryMethod.realtimeSocket')}
                                />
                            </Stack>
                        </RadioGroup>
                    </FormGroup>

                    <FormGroup className={classes.formGroup}>
                        <FormLabel>{t('iCalendar.reminder.direction')}</FormLabel>
                        <RadioGroup name="direction" value={fields.direction} onChange={handleFieldChange}>
                            <Stack direction="row" spacing={1}>
                                <FormControlLabel
                                    value="before"
                                    control={<Radio />}
                                    label={t('iCalendar.reminder.before')}
                                />
                                <FormControlLabel
                                    value="after"
                                    control={<Radio />}
                                    label={t('iCalendar.reminder.after')}
                                />
                            </Stack>
                        </RadioGroup>
                    </FormGroup>
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end" className={flexBox.paddingTop2}>
                    <ActionButton disabled={savePending} onClick={cancel} color="default" fitWidth>
                        {t('multimedia.multimediaForm.cancel')}
                    </ActionButton>

                    <ActionButton
                        disabled={savePending}
                        color="primary"
                        type="submit"
                        autoFocus
                        fitWidth
                        spinner={savePending}
                    >
                        {t('game.gameActions.save')}
                    </ActionButton>
                </Stack>
            </ValidatorForm>
        </LayoutForm>
    );
};

export default ICalendarReminderForm;
